import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const DeleteAccount = () => {
  useEffect(() => {
    document.title = 'Delete your Hound account | Hound Payments';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'How to permanently delete your Hound Payments account and associated data from within the app or by contacting support.',
    );
  }, []);

  return (
    <div className="container max-w-2xl mx-auto py-10 px-4">

      <h1 className="text-3xl font-bold mb-2">Delete your account</h1>
      <p className="text-muted-foreground mb-6">
        You can permanently delete your Hound Payments account and all
        associated data at any time. This page explains how.
      </p>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Delete from inside the app</CardTitle>
          <CardDescription>The fastest way — takes about 30 seconds.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Open the Hound Payments app and sign in.</li>
            <li>Tap your profile icon, then go to <strong>Profile</strong>.</li>
            <li>Scroll to the <strong>Delete account</strong> section at the bottom.</li>
            <li>Tap <strong>Delete my account</strong>.</li>
            <li>Type <span className="font-mono font-semibold">DELETE</span> in the confirmation box and tap <strong>Delete account</strong>.</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Request deletion by email</CardTitle>
          <CardDescription>If you can no longer sign in to the app.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>
            Email{' '}
            <a className="text-primary underline" href="mailto:support@houndpayments.co.uk">
              support@houndpayments.co.uk
            </a>{' '}
            from the address associated with your account and include the
            subject line "Delete my account". We'll verify your identity and
            delete your account within 7 days.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What gets deleted</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <ul className="list-disc list-inside space-y-1">
            <li>Your profile and login credentials</li>
            <li>Customer records you created</li>
            <li>Payment links, receipts and payment history</li>
            <li>Business settings, review links and connected platform data</li>
          </ul>
          <p className="mt-4 text-muted-foreground">
            Some information may be retained where required by law (for
            example, tax or anti-fraud records) for the legally required
            period, after which it is also deleted.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeleteAccount;
